import { ReactNode, useRef, useState, TouchEvent } from 'react';

/**
 * A roster row whose actions are reached by swiping on touch devices.
 *
 * Attendance is taken standing at the door on a phone, one hand on the device — so the
 * primary actions live behind a swipe rather than behind buttons that would crowd a
 * narrow row. Swiping LEFT drags the row aside to expose `swipeLeftActions` at the right
 * edge; swiping RIGHT exposes `swipeRightActions` at the left edge. Nothing commits on the
 * swipe itself: the revealed button still has to be tapped, so a stray gesture while
 * scrolling can never silently mark a child absent.
 *
 * Tapping the row body opens the same tray, which keeps every action reachable without a
 * gesture. Desktop does not use any of this — render ordinary buttons there instead.
 */

export interface SwipeAction {
  key: string;
  label: string;
  /** Tailwind classes for the action's background/text. */
  className: string;
  onSelect: () => void;
}

export type OpenSide = 'left' | 'right' | null;

interface Props {
  /** Revealed by swiping LEFT; rendered against the row's right edge. */
  swipeLeftActions: SwipeAction[];
  /** Revealed by swiping RIGHT; rendered against the row's left edge. */
  swipeRightActions: SwipeAction[];
  /** Which tray is open. Controlled by the parent so only one row is ever open. */
  openSide: OpenSide;
  onOpenSideChange: (side: OpenSide) => void;
  /** When true the row does not respond to swipes or taps (e.g. a non-session day). */
  disabled?: boolean;
  children: ReactNode;
}

/** Width of one action button in the tray. Wide enough for a two-word label. */
const ACTION_WIDTH = 88;

/** Fraction of the tray a drag must cross before it snaps open instead of closed. */
const SNAP_RATIO = 0.4;

/**
 * A drag is only treated as a swipe once it clears this many pixels horizontally AND is
 * more horizontal than vertical. Below it, the touch belongs to the scrolling page.
 */
const DIRECTION_LOCK_PX = 8;

export function SwipeableRow({
  swipeLeftActions,
  swipeRightActions,
  openSide,
  onOpenSideChange,
  disabled = false,
  children,
}: Props) {
  const [dragOffset, setDragOffset] = useState<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  // null = direction not yet decided, true = horizontal swipe, false = vertical scroll.
  const isHorizontal = useRef<boolean | null>(null);

  const maxLeft = swipeLeftActions.length * ACTION_WIDTH;
  const maxRight = swipeRightActions.length * ACTION_WIDTH;

  const restingOffset = openSide === 'left' ? -maxLeft : openSide === 'right' ? maxRight : 0;
  const offset = dragOffset ?? restingOffset;

  function handleTouchStart(e: TouchEvent<HTMLDivElement>) {
    if (disabled) return;
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY };
    isHorizontal.current = null;
  }

  function handleTouchMove(e: TouchEvent<HTMLDivElement>) {
    if (disabled || !start.current) return;
    const t = e.touches[0];
    const dx = t.clientX - start.current.x;
    const dy = t.clientY - start.current.y;

    if (isHorizontal.current === null) {
      if (Math.abs(dx) < DIRECTION_LOCK_PX && Math.abs(dy) < DIRECTION_LOCK_PX) return;
      isHorizontal.current = Math.abs(dx) > Math.abs(dy);
    }
    if (!isHorizontal.current) return;

    // Clamp to the trays that actually exist, so a row with no right-side action can't be
    // dragged open onto empty space.
    setDragOffset(Math.max(-maxLeft, Math.min(maxRight, restingOffset + dx)));
  }

  function handleTouchEnd() {
    if (disabled || dragOffset === null) {
      start.current = null;
      return;
    }
    const settled = dragOffset;
    setDragOffset(null);
    start.current = null;

    if (settled < 0 && maxLeft > 0 && Math.abs(settled) > maxLeft * SNAP_RATIO) {
      onOpenSideChange('left');
    } else if (settled > 0 && maxRight > 0 && settled > maxRight * SNAP_RATIO) {
      onOpenSideChange('right');
    } else {
      onOpenSideChange(null);
    }
  }

  /** Tap fallback: open whichever tray has actions, preferring the left one. */
  function handleTap() {
    if (disabled) return;
    if (openSide !== null) {
      onOpenSideChange(null);
      return;
    }
    if (swipeLeftActions.length > 0) onOpenSideChange('left');
    else if (swipeRightActions.length > 0) onOpenSideChange('right');
  }

  function runAction(action: SwipeAction) {
    onOpenSideChange(null);
    action.onSelect();
  }

  const hasActions = swipeLeftActions.length > 0 || swipeRightActions.length > 0;

  return (
    <div className="relative overflow-hidden bg-white">
      {/* Right-edge tray, revealed by swiping left. */}
      {swipeLeftActions.length > 0 && (
        <div className="absolute inset-y-0 right-0 flex">
          {swipeLeftActions.map(action => (
            <button
              key={action.key}
              type="button"
              tabIndex={openSide === 'left' ? 0 : -1}
              aria-hidden={openSide !== 'left'}
              onClick={() => runAction(action)}
              style={{ width: ACTION_WIDTH }}
              className={`flex items-center justify-center text-sm font-semibold ${action.className}`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Left-edge tray, revealed by swiping right. */}
      {swipeRightActions.length > 0 && (
        <div className="absolute inset-y-0 left-0 flex">
          {swipeRightActions.map(action => (
            <button
              key={action.key}
              type="button"
              tabIndex={openSide === 'right' ? 0 : -1}
              aria-hidden={openSide !== 'right'}
              onClick={() => runAction(action)}
              style={{ width: ACTION_WIDTH }}
              className={`flex items-center justify-center text-sm font-semibold ${action.className}`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      <div
        role={hasActions && !disabled ? 'button' : undefined}
        tabIndex={hasActions && !disabled ? 0 : undefined}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onClick={handleTap}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTap();
          }
        }}
        style={{
          transform: `translateX(${offset}px)`,
          // Animate the snap, but track the finger 1:1 while dragging.
          transition: dragOffset === null ? 'transform 180ms ease-out' : 'none',
        }}
        className="relative bg-white"
      >
        {children}
      </div>
    </div>
  );
}
